import { ClassNames } from '@emotion/react';
import { Box, css, Stack, styled, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { Layer, Popup, Source } from '@vis.gl/react-maplibre';
import dayjs from 'dayjs';
import type { Geometry } from 'geojson';
import { Fragment, useMemo, useState } from 'react';

import { PopupContent } from '@/components/PopupContent';
import { TrafficReport } from '@/modules/TrafficReport';
import { useUserStore } from '@/stores/useUserStore';
import { MapLayer } from '@/types/MapLayer';
import type { TrafficReportFeature } from '@/types/TrafficReportFeature';

export const SmallTypography = styled(Typography)(
  () => css`
    font-size: 0.625rem;
  `,
);

interface TrafficReportPopupProps {
  feature?: TrafficReportFeature;
  onClose: () => void;
}

const parseComments = (commentsString?: string | null): string[] => {
  if (!commentsString) {
    return [];
  }

  try {
    return JSON.parse(commentsString) as string[];
  } catch {
    const cleaned = commentsString.replace(/^\{+|}+$/g, '').replace(/^"+|"+$/g, '');

    return cleaned ? [cleaned] : [];
  }
};

const formatDateRange = (validFrom?: string, validTo?: string): string | null => {
  if (!validFrom && !validTo) {
    return null;
  }

  const from = validFrom ? dayjs(validFrom).format('DD.MM.YYYY') : '';
  const to = validTo ? dayjs(validTo).format('DD.MM.YYYY') : '';

  if (from && to) {
    return `${from} – ${to}`;
  }

  return from || to;
};

const parseGeometry = (geometryString: string) => {
  try {
    return JSON.parse(geometryString) as Geometry;
  } catch {
    return null;
  }
};

export function TrafficReportPopup({ feature, onClose }: TrafficReportPopupProps) {
  const theme = useTheme();
  const adminMode = useUserStore((state) => state.adminMode);
  const [geometryType, setGeometryType] = useState<'original' | 'matched'>(
    feature?.properties.matched ? 'matched' : 'original',
  );

  const geometries = useMemo(
    () => ({
      original: feature?.properties.originalGeometry ?? feature?.properties.geom_original ?? '',
      matched: feature?.properties.geom_matched ?? '',
    }),
    [feature?.properties],
  );

  const comments = useMemo(() => parseComments(feature?.properties.comments), [feature?.properties.comments]);
  const reroutingComments = useMemo(
    () => parseComments(feature?.properties.rerouting_comments),
    [feature?.properties.rerouting_comments],
  );
  const dateRange = useMemo(
    () => formatDateRange(feature?.properties.valid_from, feature?.properties.valid_to),
    [feature?.properties.valid_from, feature?.properties.valid_to],
  );

  if (!feature) {
    return null;
  }

  const [longitude, latitude] = feature.geometry.coordinates;
  const title = TrafficReport.title(feature);
  const matchingStatus = feature.properties.matched ? 'Erfolgreich' : 'Fehlgeschlagen';
  const matchingColor = feature.properties.matched ? 'success.main' : 'error.main';

  return (
    <ClassNames>
      {({ css: cssClassName }) => (
        <Fragment>
          <Popup
            key={feature.properties.id}
            anchor="top-left"
            closeButton={false}
            closeOnClick={false}
            latitude={latitude}
            longitude={longitude}
            maxWidth="294px"
            offset={24}
            onClose={onClose}
            className={cssClassName`
              .maplibregl-popup-content {
                border-radius: 0 8px 8px 8px;
                width: 294px;
              }
            `}
          >
            <PopupContent onClose={onClose}>
              <Stack spacing={2}>
                <Stack direction="column">
                  <Typography variant="h6">{title}</Typography>

                  <Stack alignItems="center" direction="row" spacing={1}>
                    <SmallTypography color="text.secondary" variant="body2">
                      ID: {feature.properties.id}
                    </SmallTypography>
                    <SmallTypography color="text.secondary" variant="body2">
                      Quelle: Lorem ipsum
                    </SmallTypography>
                    <Stack direction="row" display="inline">
                      <SmallTypography color="text.secondary" display="inline" variant="body2">
                        Matching:{' '}
                      </SmallTypography>
                      <SmallTypography color={matchingColor} display="inline" variant="body2">
                        {matchingStatus}
                      </SmallTypography>
                    </Stack>
                  </Stack>
                </Stack>

                {adminMode && geometries.original && geometries.matched ? (
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    color="primary"
                    size="small"
                    value={geometryType}
                    onChange={(_, value) => {
                      if (value) {
                        setGeometryType(value);
                      }
                    }}
                  >
                    <ToggleButton value="original">Original</ToggleButton>
                    <ToggleButton value="matched">Matched</ToggleButton>
                  </ToggleButtonGroup>
                ) : null}

                {dateRange ? (
                  <Box>
                    <Typography variant="subtitle2">Datum</Typography>
                    <Typography variant="body2">{dateRange}</Typography>
                  </Box>
                ) : null}

                {comments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2">Meldungen</Typography>
                    <Stack spacing={1}>
                      {comments.map((comment) => (
                        <Typography key={comment} variant="body2">
                          {comment}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                {reroutingComments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2">Umleitung</Typography>
                    <Stack spacing={1}>
                      {reroutingComments.map((comment) => (
                        <Typography key={comment} variant="body2">
                          {comment}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </PopupContent>
          </Popup>

          {geometries[geometryType] && parseGeometry(geometries[geometryType]) ? (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <Source data={parseGeometry(geometries[geometryType])!} type="geojson">
              <Layer
                beforeId={MapLayer.TRAFFIC_REPORT}
                type="line"
                layout={{
                  'line-cap': 'round',
                  'line-join': 'round',
                }}
                paint={{
                  'line-color': theme.palette.error.main,
                  'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 10, 2, 14, 4, 18, 8],
                  'line-dasharray': [1, 2],
                }}
              />
            </Source>
          ) : null}
        </Fragment>
      )}
    </ClassNames>
  );
}
